/**
 * Picks exactly one foreground tab to play toast/sound for a notification so
 * multiple tabs do not double-alert. Uses the Web Locks API when available and
 * falls back to a localStorage lease. On top of the lease both branches keep a
 * per-key dedupe record, so two tabs whose events arrive slightly offset still
 * alert only once. Never stores notification content — only the dedupe key
 * (type/revision) and a timestamp.
 */
const LOCK_NAME = 'notification-foreground';
const LEASE_KEY = 'notification_foreground_lease';
const LEASE_MS = 5000;
const DEDUPE_KEY = 'notification_attention_dedupe';
/** Keep dedupe records long enough to absorb a burst/reconnect replay. */
export const DEDUPE_TTL_MS = 60_000;
const DEDUPE_MAX_ENTRIES = 50;

export interface TabCoordinator {
  /**
   * Runs the callback if this tab may alert for `dedupeKey`. The key is
   * recorded before the callback runs, so two callbacks for the same key never
   * both alert — including two tabs racing on offset event delivery. Omit the
   * key for pure leader election (callers that only refresh state).
   */
  runIfLeader(fn: () => void, dedupeKey?: string): void;
  /** Record a dedupe key for an alert emitted outside `runIfLeader`. */
  commitAlert(key: string): void;
  release(): void;
}

function hasWebLocks(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { locks?: LockManager }).locks?.request === 'function'
  );
}

function readJson<T>(key: string): T | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode) */
  }
}

const currentTabId =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Returns true when a fresh lease owned by another tab exists. */
function otherTabHoldsLease(now: number): boolean {
  const lease = readJson<{ tabId: string; expiresAt: number }>(LEASE_KEY);
  if (!lease) return false;
  return lease.expiresAt > now && lease.tabId !== currentTabId;
}

/** Whether this browser already alerted `key` within the dedupe TTL. */
export function isDuplicateAlert(key: string, now: number = Date.now()): boolean {
  const record = readJson<Record<string, number>>(DEDUPE_KEY);
  if (!record || typeof record !== 'object') return false;
  const at = record[key];
  return typeof at === 'number' && now - at < DEDUPE_TTL_MS;
}

/** Records `key` as alerted, pruning stale entries. Only key+timestamp stored. */
export function markAlert(key: string, now: number = Date.now()): void {
  const record = readJson<Record<string, number>>(DEDUPE_KEY) ?? {};
  const pruned: Record<string, number> = {};
  Object.entries(record).forEach(([k, at]) => {
    if (typeof at === 'number' && now - at < DEDUPE_TTL_MS) pruned[k] = at;
  });
  pruned[key] = now;
  const entries = Object.entries(pruned)
    .sort((a, b) => b[1] - a[1])
    .slice(0, DEDUPE_MAX_ENTRIES);
  writeJson(DEDUPE_KEY, Object.fromEntries(entries));
}

export function createTabCoordinator(): TabCoordinator {
  let released = false;

  const takeLease = () => {
    writeJson(LEASE_KEY, { tabId: currentTabId, expiresAt: Date.now() + LEASE_MS });
  };

  return {
    runIfLeader(fn: () => void, dedupeKey?: string) {
      if (released) return;
      if (dedupeKey != null && isDuplicateAlert(dedupeKey)) return;

      if (hasWebLocks()) {
        // The Web Lock only serializes the synchronous callback, so it does not
        // by itself stop two tabs: the persisted lease and dedupe record do.
        (navigator.locks as LockManager)
          .request(LOCK_NAME, { ifAvailable: true }, () => {
            if (released) return;
            if (dedupeKey != null && isDuplicateAlert(dedupeKey)) return;
            if (otherTabHoldsLease(Date.now())) return;
            takeLease();
            if (dedupeKey != null) markAlert(dedupeKey);
            fn();
          })
          .catch(() => {
            /* ignore */
          });
        return;
      }

      if (otherTabHoldsLease(Date.now())) return;
      takeLease();
      if (dedupeKey != null) markAlert(dedupeKey);
      fn();
    },
    commitAlert(key: string) {
      markAlert(key);
    },
    release() {
      released = true;
      const lease = readJson<{ tabId: string }>(LEASE_KEY);
      if (lease && lease.tabId === currentTabId) {
        try {
          localStorage.removeItem(LEASE_KEY);
        } catch {
          /* ignore */
        }
      }
    },
  };
}
